import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, PERMISSION, can, inboxApi, useAuth, type Agent } from '@wa/shared';

/**
 * The kebab beside the selection count.
 *
 * Every action is one request over the whole set. Forty conversations assigned one
 * at a time is forty round trips, forty transactions and forty chances to
 * half-finish; these either happen or they do not.
 *
 * <b>There is no Delete chat, and its absence is the one deliberate difference
 * from the design being matched.</b> Messages are the record of what this company
 * told a customer — quoted in disputes, and what a DPDP request is answered from.
 * Retention removes them, on a timer, as a policy. A menu item that erases forty
 * conversations because an inbox looked untidy would be the most destructive
 * control on this screen, and it would sit two pixels below "Mark as read".
 * <b>Close</b> stands in its place, and a per-conversation <i>Clear chat</i>
 * already exists for an agent who wants a clean pane without destroying anything.
 */
interface Props {
  ids: number[];
  onDone: () => void;
}

type Sheet = null | 'tags' | 'assign' | 'lists';

export function BulkActions({ ids, onDone }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['counts'] });
  };

  const fail = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'That did not work.');

  /** Every action reports how many rows it actually changed, not how many were
      selected. Re-running one over an overlapping selection legitimately changes
      nothing, and "0 changed" is the honest answer to that. */
  const ran = (verb: string) => (result: { changed: number }) => {
    setError(null);
    setDone(`${verb} ${result.changed} of ${ids.length}.`);
    setSheet(null);
    refresh();
    if (result.changed > 0) onDone();
  };

  const markRead = useMutation({
    mutationFn: () => inboxApi.bulkRead(ids),
    onSuccess: ran('Marked read'), onError: fail,
  });
  const close = useMutation({
    mutationFn: () => inboxApi.bulkStatus(ids, 2),
    onSuccess: ran('Closed'), onError: fail,
  });
  const assign = useMutation({
    mutationFn: (agentId: number | null) => inboxApi.bulkAssign(ids, agentId),
    onSuccess: ran('Assigned'), onError: fail,
  });
  const tag = useMutation({
    mutationFn: (tagId: number) => inboxApi.bulkTag(ids, tagId),
    onSuccess: ran('Tagged'), onError: fail,
  });

  const busy = markRead.isPending || close.isPending || assign.isPending || tag.isPending;

  const items: { key: string; label: string; run: () => void; hidden?: boolean }[] = [
    { key: 'read', label: 'Mark as read', run: () => markRead.mutate() },
    { key: 'tags', label: 'Add tags', run: () => setSheet('tags'),
      hidden: !can(user, PERMISSION.inboxSend) },
    { key: 'assign', label: 'Assign agent', run: () => setSheet('assign'),
      hidden: !can(user, PERMISSION.inboxAssign) },
    { key: 'lists', label: 'Add to broadcast list', run: () => setSheet('lists'),
      hidden: !can(user, PERMISSION.segmentsWrite) },
    { key: 'unassign', label: 'Un-assign chat', run: () => assign.mutate(null),
      hidden: !can(user, PERMISSION.inboxAssign) },
    { key: 'close', label: 'Close chat', run: () => close.mutate(),
      hidden: !can(user, PERMISSION.inboxSend) },
  ].filter((i) => !i.hidden);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => { setError(null); setDone(null); setOpen((v) => !v); }}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${ids.length} selected`}
        className="tap grid size-7 place-items-center rounded-(--radius-field) text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink) disabled:opacity-50"
      >
        <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
          <circle cx="10" cy="4.2" r="1.5" fill="currentColor" />
          <circle cx="10" cy="10" r="1.5" fill="currentColor" />
          <circle cx="10" cy="15.8" r="1.5" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); item.run(); }}
              className="tap block w-full px-3 py-2 text-left text-small text-(--color-ink) transition-colors hover:bg-(--color-sunken)"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {sheet === 'tags' && (
        <Picker
          title={`Tag ${ids.length} chats`}
          queryKey={['tags']}
          load={inboxApi.tags}
          render={(t: { id: number; name: string }) => t.name}
          onPick={(t) => tag.mutate(t.id)}
          busy={busy}
          error={error}
          onClose={() => { setError(null); setSheet(null); }}
        />
      )}

      {sheet === 'assign' && (
        <Picker
          title={`Assign ${ids.length} chats`}
          queryKey={['agents']}
          load={inboxApi.agents}
          render={(a: Agent) => a.displayName}
          onPick={(a) => assign.mutate(a.id)}
          busy={busy}
          error={error}
          onClose={() => { setError(null); setSheet(null); }}
        />
      )}

      {sheet === 'lists' && (
        <BroadcastPicker ids={ids} onDone={ran('Added')} onError={fail}
                         error={error} onClose={() => { setError(null); setSheet(null); }} />
      )}

      {/* Below the control rather than inside a dialog. It reports on something
          already finished, and a dialog for that is a click nobody wanted. */}
      {(done || error) && !sheet && (
        <p
          role="status"
          className={[
            'absolute right-0 top-full z-30 mt-1 w-56 rounded-(--radius-field) border px-2.5 py-1.5 text-micro shadow-sm',
            error
              ? 'border-(--color-bad) bg-(--color-surface) text-(--color-bad)'
              : 'border-(--color-line) bg-(--color-surface) text-(--color-muted)',
          ].join(' ')}
        >
          {error ?? done}
        </p>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- pickers -- */

function Picker<T extends { id: number }>({
  title, queryKey, load, render, onPick, busy, error, onClose,
}: {
  title: string;
  queryKey: string[];
  load: () => Promise<T[]>;
  render: (item: T) => string;
  onPick: (item: T) => void;
  busy: boolean;
  error: string | null;
  onClose: () => void;
}) {
  const list = useQuery({ queryKey, queryFn: load, staleTime: 300_000 });

  return (
    <Modal title={title} onClose={onClose}>
      {list.isPending && <p className="text-small text-(--color-muted)">Loading…</p>}
      {!list.isPending && (list.data ?? []).length === 0 && (
        <p className="text-small text-(--color-muted)">There are none to choose from yet.</p>
      )}
      <ul className="flex flex-col gap-1">
        {(list.data ?? []).map((item) => (
          <li key={item.id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => onPick(item)}
              className="tap w-full rounded-md px-2.5 py-2 text-left text-small hover:bg-(--color-sunken) disabled:opacity-60"
            >
              {render(item)}
            </button>
          </li>
        ))}
      </ul>
      {error && <p role="alert" className="mt-2 text-small text-(--color-bad)">{error}</p>}
    </Modal>
  );
}

/**
 * "Add to broadcast list" is a contact-level act, so it takes the contacts behind
 * the selected conversations rather than the conversations themselves — and only
 * static lists are offered, because a rule-based list is defined by its rules and
 * adding forty people to one by hand would be a lie about what it contains.
 */
function BroadcastPicker({ ids, onDone, onError, error, onClose }: {
  ids: number[];
  onDone: (result: { changed: number }) => void;
  onError: (e: unknown) => void;
  error: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const segments = useQuery({ queryKey: ['segments'], queryFn: inboxApi.segments, staleTime: 300_000 });

  // The contact ids are already on the rows the page is holding, so this reads
  // them out of the cache rather than asking for forty conversations again.
  const contactIds = (queryClient.getQueriesData<{ items: { id: number; contactId: number }[] }>({ queryKey: ['conversations'] })
    .flatMap(([, data]) => data?.items ?? [])
    .filter((c) => ids.includes(c.id))
    .map((c) => c.contactId));

  const unique = [...new Set(contactIds)];

  const add = useMutation({
    mutationFn: (segmentId: number) => inboxApi.addSegmentMembers(segmentId, unique),
    onSuccess: () => onDone({ changed: unique.length }),
    onError,
  });

  const statics = (segments.data ?? []).filter((s) => s.kind === 0);

  return (
    <Modal title={`Add ${unique.length} contacts to a list`} onClose={onClose}>
      {segments.isPending && <p className="text-small text-(--color-muted)">Loading…</p>}

      {!segments.isPending && statics.length === 0 && (
        <p className="text-small text-(--color-muted)">
          There are no fixed lists yet. Rule-based lists decide their own members, so
          nobody can be added to one by hand — make a fixed list under Customers.
        </p>
      )}

      <ul className="flex flex-col gap-1">
        {statics.map((segment) => (
          <li key={segment.id}>
            <button
              type="button"
              disabled={add.isPending || unique.length === 0}
              onClick={() => add.mutate(segment.id)}
              className="tap w-full rounded-md px-2.5 py-2 text-left text-small hover:bg-(--color-sunken) disabled:opacity-60"
            >
              {segment.name}
            </button>
          </li>
        ))}
      </ul>

      {error && <p role="alert" className="mt-2 text-small text-(--color-bad)">{error}</p>}
    </Modal>
  );
}

function Modal({ title, children, onClose }: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/35 p-4" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[70vh] w-full max-w-xs overflow-y-auto rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) p-4 shadow-(--shadow-pop)"
      >
        <h2 className="mb-2 text-lead font-semibold tracking-tight">{title}</h2>
        {children}
      </div>
    </div>
  );
}
